import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import { listSecrets, expireSecret } from '@/api/secrets';
import { getAudit } from '@/api/audit';
import { ApiError } from '@/api/client';
import { RequireRole } from '@/auth/RequireRole';
import { useAuth } from '@/auth/useAuth';
import { AlertDialog } from '@/components/ui/AlertDialog';
import { formatDateTime } from '@/lib/dateFormat';
import type { SecretListItem } from '@/types/oscar';
import { OnPassTabs } from './OnPassTabs';
import { CopyButton } from './CopyButton';

export function OnPassDashboard() {
  return (
    <section>
      <h1 className="font-heading text-2xl font-bold text-navy">OnPass — my secrets</h1>
      <OnPassTabs />
      <RequireRole anyOf={['oscar-admin', 'oscar-helpdesk', 'onpass-user']}>
        <DashboardBody />
      </RequireRole>
    </section>
  );
}

function DashboardBody() {
  const queryClient = useQueryClient();
  const { isAdmin } = useAuth();
  const [toExpire, setToExpire] = useState<SecretListItem | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['secrets'],
    queryFn: ({ signal }) => listSecrets(signal),
    retry: false,
  });

  const expireMutation = useMutation({
    mutationFn: (token: string) => expireSecret(token),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['secrets'] }),
    onError: (err) => setActionError(err instanceof ApiError ? err.message : 'Could not expire the secret.'),
  });

  const items = query.data ?? [];

  return (
    <div className="max-w-4xl">
      {actionError && (
        <div role="alert" className="mb-3 rounded border border-red/30 bg-red/10 px-3 py-2 text-sm text-red">
          {actionError}
        </div>
      )}

      {query.isLoading ? (
        <p className="text-sm text-darkGrey">Loading…</p>
      ) : query.isError ? (
        <p role="alert" className="text-sm text-red">
          {query.error instanceof ApiError ? query.error.message : 'Could not load your secrets.'}
        </p>
      ) : items.length === 0 ? (
        <p className="text-sm text-darkGrey">
          You have no active secrets.{' '}
          <Link to="/onpass" className="font-semibold text-navy hover:underline">Share one</Link>.
        </p>
      ) : (
        <div className="overflow-x-auto rounded border border-border">
          <table className="min-w-full divide-y divide-border text-sm">
            <thead className="bg-bg text-left text-xs font-semibold uppercase text-darkGrey">
              <tr>
                <th className="px-3 py-2">Created</th>
                <th className="px-3 py-2">Expires</th>
                <th className="px-3 py-2">Views left</th>
                <th className="px-3 py-2">Passphrase</th>
                <th className="px-3 py-2">Locked to</th>
                <th className="px-3 py-2">Link</th>
                <th className="px-3 py-2"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border bg-white">
              {items.map((s) => (
                <tr key={s.Token}>
                  <td className="whitespace-nowrap px-3 py-2">{formatDateTime(s.CreatedDate)}</td>
                  <td className="whitespace-nowrap px-3 py-2">{formatDateTime(s.ExpiresAtUtc)}</td>
                  <td className="px-3 py-2">
                    {s.MaxViews == null ? 'Unlimited' : `${s.ViewsRemaining} / ${s.MaxViews}`}
                  </td>
                  <td className="px-3 py-2">
                    {s.HasPassphrase
                      ? <span className="inline-flex items-center gap-1 text-navy"><Lock className="h-3.5 w-3.5" aria-hidden /> Yes</span>
                      : <span className="text-darkGrey/60">No</span>}
                  </td>
                  <td className="px-3 py-2">
                    {s.LockedToName
                      ? <span className="inline-flex items-center gap-1 text-navy"><Lock className="h-3.5 w-3.5" aria-hidden /> {s.LockedToName}</span>
                      : <span className="text-darkGrey/60">Anyone with link</span>}
                  </td>
                  <td className="px-3 py-2"><CopyButton value={s.Url} label="Copy link" /></td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => { setActionError(null); setToExpire(s); }}
                      className="rounded border border-red/40 px-2.5 py-1 text-sm font-medium text-red hover:bg-red/10"
                    >
                      Expire
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isAdmin && <AuditTrail />}

      <AlertDialog
        open={toExpire !== null}
        onOpenChange={(o) => { if (!o) setToExpire(null); }}
        title="Expire this secret now?"
        description="The encrypted payload is destroyed immediately and the link will stop working. The audit record is kept. This cannot be undone."
        confirmLabel="Expire now"
        destructive
        onConfirm={() => { if (toExpire) expireMutation.mutate(toExpire.Token); setToExpire(null); }}
      />
    </div>
  );
}

/** Admin-only OnPass audit trail (Feature = OnPass), loaded on demand. */
function AuditTrail() {
  const [open, setOpen] = useState(false);
  const query = useQuery({
    queryKey: ['audit', 'OnPass'],
    queryFn: ({ signal }) => getAudit({ feature: 'OnPass', take: 50 }, signal),
    enabled: open,
    retry: false,
  });

  return (
    <div className="mt-6">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="rounded border border-border bg-white px-3 py-1.5 text-sm font-medium text-darkGrey hover:bg-bg"
      >
        {open ? 'Hide audit trail' : 'Show OnPass audit trail'}
      </button>

      {open && (
        <div className="mt-3 overflow-x-auto rounded border border-border">
          {query.isLoading ? (
            <p className="p-3 text-sm text-darkGrey">Loading…</p>
          ) : query.isError ? (
            <p role="alert" className="p-3 text-sm text-red">Could not load the audit trail.</p>
          ) : (query.data ?? []).length === 0 ? (
            <p className="p-3 text-sm text-darkGrey">No OnPass audit entries.</p>
          ) : (
            <table className="min-w-full divide-y divide-border text-sm">
              <thead className="bg-bg text-left text-xs font-semibold uppercase text-darkGrey">
                <tr>
                  <th className="px-3 py-2">When</th>
                  <th className="px-3 py-2">Action</th>
                  <th className="px-3 py-2">Secret</th>
                  <th className="px-3 py-2">By</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-white">
                {(query.data ?? []).map((a) => (
                  <tr key={a.AuditId}>
                    <td className="whitespace-nowrap px-3 py-2">{formatDateTime(a.ChangedDate)}</td>
                    <td className="px-3 py-2">{a.Action}</td>
                    <td className="px-3 py-2">{a.EntityId ? `#${a.EntityId}` : '—'}</td>
                    <td className="px-3 py-2">{a.ChangedBy}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
